import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  ApiError,
  askAssistant,
  getAssistantStatus,
  type AskAssistantResult,
  type AssistantErrorCode,
} from '../../lib/api';
import { useApi } from '../../lib/useApi';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import styles from './AssistantWidget.module.css';

const MAX_QUESTION_LENGTH = 2000;

const EXAMPLE_PROMPTS = [
  'How many onboarders are complete this week?',
  'Which contracts are still unsigned?',
  'How do I send a contract to a vendor?',
];

/** R23 — fixed, code-driven error copy. Never the raw upstream message.
 *  Mirrors AssistantPage's ERROR_MESSAGES exactly. */
const ERROR_MESSAGES: Record<AssistantErrorCode, string> = {
  ASSISTANT_UNAVAILABLE: 'The assistant is not configured on this server.',
  ASSISTANT_TIMEOUT: 'The assistant took too long to answer. Try a shorter question.',
  ASSISTANT_RATE_LIMITED: 'The assistant is busy right now. Wait a minute and try again.',
  ASSISTANT_REFUSED: 'The assistant declined to answer that question.',
  ASSISTANT_EMPTY: 'The assistant could not answer right now. Try again.',
  ASSISTANT_UPSTREAM_ERROR: 'The assistant could not answer right now. Try again.',
};

/** ASSISTANT_UNAVAILABLE means "no key on the server" — retrying changes nothing. */
function isRetryable(code: AssistantErrorCode): boolean {
  return code !== 'ASSISTANT_UNAVAILABLE';
}

type SuccessResult = Extract<AskAssistantResult, { answer: string }>;
type ErrorResult = Extract<AskAssistantResult, { error: unknown }>;

/**
 * AssistantWidget — S44. Floating corner Help-assistant chat bubble mounted
 * once inside AppShell for Admin/Staff. Single-turn: reuses the exact
 * askAssistant/getAssistantStatus flow from AssistantPage, including its
 * XSS-safe plain-text answer rendering (never dangerouslySetInnerHTML) and
 * fixed per-code error copy. The full /assistant page is unaffected.
 */
export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const { data: status, error: statusError } = useApi(() => getAssistantStatus(), []);

  const [question, setQuestion] = useState('');
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<AskAssistantResult | null>(null);
  const [transportError, setTransportError] = useState<string | null>(null);
  const [lastAsked, setLastAsked] = useState('');

  const bubbleRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const configured = status?.configured ?? false;
  const trimmedQuestion = question.trim();
  const askDisabled = pending || !configured || trimmedQuestion.length === 0;

  // Focus into the panel on open, and back to the bubble on close.
  useEffect(() => {
    if (open) {
      textareaRef.current?.focus();
    } else {
      bubbleRef.current?.focus();
    }
  }, [open]);

  // Escape closes the panel, from anywhere inside it.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  async function submitQuestion(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    setPending(true);
    setTransportError(null);
    setLastAsked(trimmed);
    try {
      const res = await askAssistant(trimmed);
      setResult(res);
    } catch (err) {
      setResult(null);
      setTransportError(err instanceof ApiError ? err.message : 'Unable to reach the assistant.');
    } finally {
      setPending(false);
    }
  }

  function handleAsk() {
    void submitQuestion(question);
  }

  function handleChipClick(prompt: string) {
    setQuestion(prompt);
    textareaRef.current?.focus();
  }

  function handleRetry() {
    void submitQuestion(lastAsked);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!askDisabled) handleAsk();
    }
  }

  function handleToggle() {
    setOpen((prev) => !prev);
  }

  function handleClose() {
    setOpen(false);
  }

  function resetConversation() {
    setQuestion('');
    setResult(null);
    setTransportError(null);
    setLastAsked('');
  }

  const errorResult = result && 'error' in result ? (result as ErrorResult) : null;
  const successResult = result && 'answer' in result ? (result as SuccessResult) : null;
  const notConfigured = !statusError && status && !configured;

  return (
    <div className={styles.wrap}>
      {open && (
        <Card
          className={styles.panel}
          role="dialog"
          aria-modal="true"
          aria-labelledby="assistant-widget-title"
        >
          <div className={styles.panelHeader}>
            <h2 id="assistant-widget-title" className={styles.panelTitle}>
              Help assistant
            </h2>
            <button
              type="button"
              className={styles.closeButton}
              aria-label="Close help assistant"
              onClick={handleClose}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className={styles.panelBody}>
            {statusError && <p className={styles.errorText} role="alert">{statusError}</p>}

            {notConfigured && (
              <p className={styles.helperText}>
                The assistant is not configured on this server. An administrator needs to set
                OPENROUTER_API_KEY on the API server.
              </p>
            )}

            {!statusError && (
              <>
                <label className={styles.label} htmlFor="assistant-widget-question">
                  Your question
                </label>
                <textarea
                  id="assistant-widget-question"
                  ref={textareaRef}
                  className={styles.textarea}
                  rows={3}
                  maxLength={MAX_QUESTION_LENGTH}
                  value={question}
                  readOnly={pending}
                  disabled={!configured}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={handleKeyDown}
                />

                <p className={styles.helperText}>
                  Do not include Social Security numbers, dates of birth or other personal details in your
                  question.
                </p>

                {!pending && !result && !transportError && configured && (
                  <div className={styles.tryBox}>
                    <p className={styles.tryLabel}>Try:</p>
                    <div className={styles.chipRow}>
                      {EXAMPLE_PROMPTS.map((prompt) => (
                        <button
                          key={prompt}
                          type="button"
                          className={styles.chip}
                          onClick={() => handleChipClick(prompt)}
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className={styles.actionsRow}>
                  <Button onClick={handleAsk} disabled={askDisabled} loading={pending}>
                    Ask
                  </Button>
                  {(successResult || errorResult || transportError) && !pending && (
                    <Button variant="secondary" onClick={resetConversation}>
                      Ask another
                    </Button>
                  )}
                </div>

                {pending && (
                  <div className={styles.centerBox}>
                    <Spinner size="md" label="Asking the assistant" />
                  </div>
                )}

                {!pending && transportError && (
                  <div className={styles.errorBox} role="alert">
                    <p className={styles.errorText}>{transportError}</p>
                    <Button variant="secondary" onClick={handleRetry}>
                      Try again
                    </Button>
                  </div>
                )}

                {!pending && !transportError && errorResult && (
                  <div className={styles.errorBox} role="alert">
                    <p className={styles.errorText}>{ERROR_MESSAGES[errorResult.error.code]}</p>
                    {isRetryable(errorResult.error.code) && (
                      <Button variant="secondary" onClick={handleRetry}>
                        Try again
                      </Button>
                    )}
                  </div>
                )}

                {!pending && !transportError && successResult && (
                  <div className={styles.answerBox} aria-live="polite">
                    <div className={styles.answerBody}>
                      {successResult.answer.split(/\n{2,}/).map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                    <p className={styles.caption}>
                      Answered by {successResult.model} · single-turn, nothing is saved
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      )}

      <button
        ref={bubbleRef}
        type="button"
        className={styles.bubble}
        aria-label={open ? 'Close help assistant panel' : 'Open help assistant'}
        aria-expanded={open}
        onClick={handleToggle}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 10h8M8 14h5M21 12a8 8 0 01-11.6 7.1L4 20l1-4.4A8 8 0 1121 12z" />
        </svg>
      </button>
    </div>
  );
}
